'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';

import { BUTTON_VARIANT_CLASSES } from '@app/constants/ui';
import { cn } from '@app/libs/utils';
import { Z_INDEX } from '@app/libs/zIndex';

import { Button } from '../Button';
import { Modal } from '../Modal';

import { ConfirmProps, ConfirmTooltipProps, ConfirmModalProps } from './types';

const getButtonVariantClasses = (variant: string = 'primary', outline: boolean = false) => {
  return BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES]?.[
    outline ? 'outline' : 'default'
  ];
};

// Component cho Tooltip Confirm
const ConfirmTooltip: React.FC<ConfirmTooltipProps> = ({
  title,
  message,
  confirmAction,
  cancelAction,
  children,
  position = 'top',
  theme = 'default',
  className,
  disabled = false,
  onOpen,
  onClose,
  ignored = false,
  triggerClassName,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const showTooltip = () => {
    if (disabled) return;
    if (ignored) {
      confirmAction.onClick();
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(true);
    onOpen?.();
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    onClose?.();
  };

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top + scrollTop - tooltipRect.height - 8;
        left = triggerRect.left + scrollLeft + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollTop + 8;
        left = triggerRect.left + scrollLeft + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + scrollTop + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left + scrollLeft - tooltipRect.width - 8;
        break;
      case 'right':
        top = triggerRect.top + scrollTop + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + scrollLeft + 8;
        break;
    }

    // Đảm bảo tooltip không bị tràn ra ngoài viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 8) left = 8;
    if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    if (top < 8) top = 8;
    if (top + tooltipRect.height > viewportHeight + scrollTop - 8) {
      top = viewportHeight + scrollTop - tooltipRect.height - 8;
    }

    setTooltipPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();
      const handleClickOutside = (event: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target as any) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target as any)
        ) {
          setIsVisible(false);
          onClose?.();
        }
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, position, onClose]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getArrowClasses = () => {
    const baseClasses = `absolute w-2 h-2 ${getThemeClasses().arrow} transform rotate-45`;

    switch (position) {
      case 'top':
        return `${baseClasses} -bottom-1`;
      case 'bottom':
        return `${baseClasses} -top-1`;
      case 'left':
        return `${baseClasses} -right-1`;
      case 'right':
        return `${baseClasses} -left-1`;
      default:
        return baseClasses;
    }
  };

  const getArrowPosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return {};

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const triggerCenterX = triggerRect.left + scrollLeft + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + scrollTop + triggerRect.height / 2;
    const tooltipLeft = tooltipPosition.left;
    const tooltipTop = tooltipPosition.top;
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    // Tính toán vị trí mũi tên để trỏ vào giữa trigger
    const arrowLeft = triggerCenterX - tooltipLeft - 4; // 4px = w-2/2 để căn giữa
    const arrowTop = triggerCenterY - tooltipTop - 4; // 4px = h-2/2 để căn giữa

    switch (position) {
      case 'top':
      case 'bottom':
        return {
          left: `${Math.max(8, Math.min(arrowLeft, tooltipWidth - 8))}px`,
        };
      case 'left':
      case 'right':
        return {
          top: `${Math.max(8, Math.min(arrowTop, tooltipHeight - 8))}px`,
        };
      default:
        return {};
    }
  };

  const getThemeClasses = useCallback(() => {
    const themes = {
      default: {
        container: 'bg-gray-900 text-white',
        title: 'text-white',
        message: 'text-gray-200',
        arrow: 'bg-gray-900',
      },
      warning: {
        container: 'bg-orange-100 text-orange-700',
        title: 'text-orange-700',
        message: 'text-orange-700',
        arrow: 'bg-orange-100',
      },
      danger: {
        container: 'bg-red-500 text-white',
        title: 'text-white',
        message: 'text-red-100',
        arrow: 'bg-red-500',
      },
      success: {
        container: 'bg-green-500 text-white',
        title: 'text-white',
        message: 'text-green-100',
        arrow: 'bg-green-500',
      },
      primary: {
        container: 'bg-blue-500 text-white',
        title: 'text-white',
        message: 'text-blue-100',
        arrow: 'bg-blue-500',
      },
      none: {
        container: 'bg-white text-gray-900 shadow-md border border-gray-200',
        title: 'text-gray-900',
        message: 'text-gray-900',
        arrow: 'bg-white border-gray-200 shadow-md',
      },
    };

    return themes[theme as keyof typeof themes] || themes.default;
  }, [theme]);

  const handleConfirm = async (e: React.MouseEvent) => {
    try {
      setIsLoading(true);
      e.preventDefault();
      e.stopPropagation();
      const result = confirmAction.onClick();
      if (result instanceof Promise) {
        await result;
      }
    } finally {
      setIsLoading(false);
      setIsVisible(false);
      onClose?.();
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    cancelAction?.onClick();
    setIsVisible(false);
    onClose?.();
  };

  return (
    <div ref={triggerRef} className={cn('inline-block', triggerClassName)} onClick={showTooltip}>
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={cn(
            'fixed p-4 rounded-lg shadow-lg min-w-64',
            getThemeClasses().container,
            className
          )}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: Z_INDEX.modal + 1,
          }}
        >
          {title && (
            <h3 className={cn('font-semibold mb-2 text-sm', getThemeClasses().title)}>{title}</h3>
          )}
          <p className={cn('mb-4', getThemeClasses().message)}>
            {typeof message === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: message }} />
            ) : (
              message
            )}
          </p>

          <div className="flex gap-2 justify-end">
            {cancelAction && (
              <Button
                variant={cancelAction.variant as any}
                outline={cancelAction.outline}
                onClick={handleCancel}
                disabled={cancelAction.disabled}
                className={cn(cancelAction.className)}
              >
                {cancelAction.label}
              </Button>
            )}
            <Button
              variant={confirmAction.variant as any}
              outline={confirmAction.outline}
              onClick={handleConfirm}
              disabled={confirmAction.disabled}
              className={cn(
                'px-3 py-1 text-xs rounded-lg cursor-pointer',
                confirmAction.className,
                getButtonVariantClasses(confirmAction.variant, confirmAction.outline)
              )}
              loadingText={'Đang xử lý'}
            >
              {confirmAction.label}
            </Button>
          </div>

          <div className={getArrowClasses()} style={getArrowPosition()} />
        </div>
      )}
    </div>
  );
};

// Component cho Modal Confirm
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmAction,
  cancelAction,
  children,
  size = 'md',
  theme = 'default',
  className,
  disabled = false,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  onOpen,
  onClose,
  ignored = false,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    if (disabled) return;
    if (ignored) {
      confirmAction.onClick();
      return;
    }
    setIsOpen(true);
    onOpen?.();
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const getThemeClasses = () => {
    const themes = {
      default: {
        title: 'text-gray-900',
        message: 'text-gray-700',
      },
      warning: {
        title: 'text-orange-800',
        message: 'text-orange-700',
      },
      danger: {
        title: 'text-red-800',
        message: 'text-red-700',
      },
      success: {
        title: 'text-green-800',
        message: 'text-green-700',
      },
      primary: {
        title: 'text-blue-800',
        message: 'text-blue-700',
      },
    };
    return themes[theme as keyof typeof themes] || themes.default;
  };

  const handleConfirm = async () => {
    const result = confirmAction.onClick();
    if (result instanceof Promise) {
      await result;
    }

    handleClose();
  };

  const handleCancel = () => {
    cancelAction?.onClick();
    handleClose();
  };

  return (
    <>
      <div onClick={handleOpen} className={cn(triggerClassName)}>
        {children}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        size={size}
        closeOnBackdropClick={closeOnBackdropClick}
        closeOnEscape={closeOnEscape}
        contentClassName={className}
      >
        <div className="space-y-4">
          <p className={cn('', getThemeClasses().message)}>
            {typeof message === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: message }} />
            ) : (
              message
            )}
          </p>

          <div className={cn('flex gap-3 justify-end pt-4')}>
            {cancelAction && (
              <Button
                onClick={handleCancel}
                disabled={cancelAction.disabled}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm cursor-pointer rounded-lg',
                  getButtonVariantClasses(cancelAction.variant, cancelAction.outline)
                )}
              >
                {cancelAction.label}
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              disabled={confirmAction.disabled || confirmAction.loading}
              className={cn(
                'px-4 py-2 rounded-lg text-sm cursor-pointer rounded-lg',
                getButtonVariantClasses(confirmAction.variant, confirmAction.outline)
              )}
            >
              {confirmAction.loading ? 'Đang xử lý...' : confirmAction.label}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// Component chính
const Confirm: React.FC<ConfirmProps> = props => {
  if (props.type === 'tooltip') {
    return <ConfirmTooltip {...(props as ConfirmTooltipProps)} />;
  }

  return <ConfirmModal {...(props as ConfirmModalProps)} />;
};

export default Confirm;
export { ConfirmTooltip, ConfirmModal };
